<template>
  <div>
    <div v-if="loading" class="flex justify-center py-10">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <template v-else>
      <template v-if="categories.length > 0">
        <div class="category-grand-total">
          <v-sheet border rounded="lg" class="category-grand-total__sheet h-full p-4 flex flex-col">
            <div class="category-grand-total__value">{{ grandFigures.usedLabel }}</div>
            <div class="category-grand-total__label">{{ t("categories.table.used") }}</div>
          </v-sheet>
          <v-sheet border rounded="lg" class="category-grand-total__sheet h-full p-4 flex flex-col">
            <div class="category-grand-total__value">{{ grandFigures.targetLabel }}</div>
            <div class="category-grand-total__label">{{ t("categories.table.target") }}</div>
          </v-sheet>
          <v-sheet border rounded="lg" class="category-grand-total__sheet h-full p-4 flex flex-col">
            <div class="category-grand-total__value" :class="grandFigures.remainingClass">
              {{ grandFigures.remainingLabel }}
            </div>
            <div class="category-grand-total__label">{{ t("categories.table.remaining") }}</div>
          </v-sheet>
          <v-sheet border rounded="lg" class="category-grand-total__sheet h-full p-4 flex flex-col">
            <div class="flex items-center gap-3 category-grand-total__progress-row">
              <v-progress-linear
                :model-value="grandFigures.barValue"
                :color="grandFigures.progressColor"
                height="8"
                rounded
                class="category-grand-total__bar"
              />
              <span class="category-grand-total__pct" :class="grandFigures.percentageClass">
                {{ grandFigures.percentageLabel }}%
              </span>
            </div>
            <div class="category-grand-total__label">{{ t("categories.table.progress") }}</div>
          </v-sheet>
        </div>

        <div class="flex items-center justify-between gap-4 flex-wrap mb-5">
          <div class="text-2xl font-bold">
            {{ t("categories.table.sectionTitle") }}
          </div>
          <div class="flex gap-2 shrink-0">
            <v-btn
              prepend-icon="mdi-download"
              border
              :loading="exportingExcel"
              :disabled="exportingExcel"
              @click="handleExportExcel"
            >
              {{ t("categories.exportExcel") }}
            </v-btn>
            <v-btn
              v-if="user.hasRole(supervisorRoles, { account })"
              prepend-icon="mdi-cog-outline"
              border
              @click="showManageDialog = true"
            >
              {{ t("categories.manage.button") }}
            </v-btn>
          </div>
        </div>
      </template>

      <div v-if="categories.length === 0" class="text-center py-10">
        <v-icon icon="mdi-tag-multiple-outline" size="48" class="text-medium-emphasis mb-3" />
        <div class="text-medium-emphasis mb-6">
          {{ t(`categories.emptyState.${props.i18nPrefix}`) }}
        </div>
        <div
          v-if="user.hasRole(supervisorRoles, { account })"
          class="flex flex-col items-center gap-3"
        >
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-plus"
            @click="showManageDialog = true"
          >
            {{ t("categories.emptyCreate") }}
          </v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-import" @click="showImportDialog = true">
            {{ t("categories.import") }}
          </v-btn>
          <v-btn
            variant="outlined"
            prepend-icon="mdi-file-upload-outline"
            @click="showAIDialog = true"
          >
            {{ t("categories.ai.button") }}
          </v-btn>
        </div>
      </div>

      <v-data-table
        v-else-if="categories.length > 0"
        hover
        :headers="headers"
        :items="rowsWithGroupKey"
        :group-by="groupBy"
        :items-per-page="-1"
        hide-default-footer
        :header-props="{ class: 'font-bold' }"
      >
        <template #group-header="{ item, toggleGroup, isGroupOpen }">
          <tr class="group-header-row">
            <td>
              <div class="flex items-center gap-2">
                <v-btn
                  :icon="isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                  size="small"
                  variant="text"
                  density="comfortable"
                  @click="toggleGroup(item)"
                ></v-btn>
                <span class="font-bold">{{ groupViews[item.value]?.name }}</span>
              </div>
            </td>
            <td class="font-bold">{{ groupViews[item.value]?.usedLabel }}</td>
            <td class="font-bold">{{ groupViews[item.value]?.targetLabel }}</td>
            <td class="font-bold">
              <span :class="groupViews[item.value]?.remainingClass">
                {{ groupViews[item.value]?.remainingLabel }}
              </span>
            </td>
            <td>
              <div class="flex items-center gap-3" style="min-width: 140px">
                <v-progress-linear
                  :model-value="groupViews[item.value]?.barValue"
                  :color="groupViews[item.value]?.progressColor"
                  height="8"
                  rounded
                  style="flex: 1"
                ></v-progress-linear>
                <span
                  class="text-sm font-bold"
                  :class="groupViews[item.value]?.percentageClass"
                  style="min-width: 45px; text-align: right"
                >
                  {{ groupViews[item.value]?.percentageLabel }}%
                </span>
              </div>
            </td>
            <td></td>
          </tr>
        </template>

        <template #item="{ item, props: rowProps }">
          <RowLink v-bind="rowProps" :to="categoryRoute(item.id)">
            <RowLinkCell focusable>
              <div class="flex items-center gap-2" :class="{ 'ps-10': hasAnyGroup }">
                <span class="font-medium">{{ item.name }}</span>
                <v-chip v-if="item.showCompleted" color="#4CAF50" size="x-small" variant="tonal">
                  {{ t("categories.completed") }}
                </v-chip>
                <v-chip v-if="item.overTarget" color="error" size="x-small" variant="tonal">
                  {{ t("categories.overTarget") }}
                </v-chip>
              </div>
            </RowLinkCell>
            <RowLinkCell>{{ item.usedLabel }}</RowLinkCell>
            <RowLinkCell>{{ item.targetLabel }}</RowLinkCell>
            <RowLinkCell>
              <span :class="item.remainingClass">{{ item.remainingLabel }}</span>
            </RowLinkCell>
            <RowLinkCell>
              <div class="flex items-center gap-3" style="min-width: 140px">
                <v-progress-linear
                  :model-value="item.barValue"
                  :color="item.progressColor"
                  height="8"
                  rounded
                  style="flex: 1"
                ></v-progress-linear>
                <span
                  class="text-sm"
                  :class="[item.percentageClass, { 'font-medium': item.overTarget }]"
                  style="min-width: 45px; text-align: right"
                >
                  {{ item.percentageLabel }}%
                </span>
              </div>
            </RowLinkCell>
            <RowLinkCell>
              <div class="flex justify-end gap-1 relative category-row-actions" @click.stop>
                <v-tooltip v-if="user.hasRole(supervisorRoles, { account })" location="top">
                  <template #activator="{ props: tooltipProps }">
                    <v-btn
                      v-bind="tooltipProps"
                      icon="mdi-cash-plus"
                      size="small"
                      variant="text"
                      class="display-on-row-hover"
                      :disabled="updatingCategoryId === item.id"
                      @click="openInitialAmountDialog(item)"
                    ></v-btn>
                  </template>
                  {{ t("categories.editInitialAmount") }}
                </v-tooltip>
                <v-tooltip v-if="user.hasRole(supervisorRoles, { account })" location="top">
                  <template #activator="{ props: tooltipProps }">
                    <v-btn
                      v-bind="tooltipProps"
                      :icon="item.completedAt ? 'mdi-check-circle' : 'mdi-check-circle-outline'"
                      size="small"
                      variant="text"
                      :color="item.completedAt ? '#4CAF50' : undefined"
                      class="display-on-row-hover"
                      :loading="updatingCategoryId === item.id"
                      :disabled="updatingCategoryId === item.id"
                      @click="toggleCompleted(item)"
                    ></v-btn>
                  </template>
                  {{
                    item.completedAt
                      ? t("categories.markIncomplete")
                      : t("categories.markCompleted")
                  }}
                </v-tooltip>
                <v-btn
                  icon="mdi-eye"
                  size="small"
                  variant="text"
                  class="display-on-row-hover"
                  :to="categoryRoute(item.id)"
                ></v-btn>
              </div>
            </RowLinkCell>
          </RowLink>
        </template>
      </v-data-table>
    </template>

    <ManageCategoriesDialog
      v-model="showManageDialog"
      :account-id="accountId"
      :direction="direction"
      :categories="categories"
      :currency-code="currencyCode ?? 'EUR'"
      :seed-categories="pendingSeeds"
      @seeds-consumed="pendingSeeds = []"
    />

    <ImportCategoriesDialog
      v-model="showImportDialog"
      :account-id="accountId"
      :direction="direction"
      @import:categories="onCategoriesImported"
    />

    <AICategoriesDialog
      v-model="showAIDialog"
      :account-id="accountId"
      :direction="direction"
      @import:categories="onCategoriesImported"
    />

    <EditInitialAmountDialog
      v-model="showInitialAmountDialog"
      :category="categoryForInitialAmount"
      :saving="updatingCategoryId === categoryForInitialAmount?.id"
      @save="saveInitialAmount"
    />
  </div>
</template>

<script lang="ts" setup>
import RowLink from "@/components/RowLink/RowLink.vue";
import RowLinkCell from "@/components/RowLink/RowLinkCell.vue";
import { useAccountCurrency } from "@/composables/useAccountCurrency.ts";
import { useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useTrackedCategories } from "@/composables/useTrackedCategories";
import { db } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import type { Account } from "@aircap/common/account";
import { supervisorRoles } from "@aircap/common/auth";
import type { TrackedCategory, TrackedCategoryDirection } from "@aircap/common/trackedCategory";
import { captureException } from "@sentry/vue";
import { collection, deleteField, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { computed, ref, toRef } from "vue";
import AICategoriesDialog from "./components/AICategoriesDialog.vue";
import EditInitialAmountDialog from "./components/EditInitialAmountDialog.vue";
import ImportCategoriesDialog from "./components/ImportCategoriesDialog.vue";
import ManageCategoriesDialog from "./components/ManageCategoriesDialog.vue";
import { downloadCategoriesXlsxFile } from "./downloadCategoriesXlsx";
import type { TrackedCategoryRow } from "./types";

const props = defineProps<{
  accountId: string;
  direction: TrackedCategoryDirection;
  i18nPrefix: "budget" | "commercialisation";
}>();

const { t, locale } = useI18n();
const user = useUserStore();
const { showErrorToast } = useToast();

const { categories, groups, loading } = useTrackedCategories(
  toRef(() => props.accountId),
  props.direction,
);

const { currencyCode } = useAccountCurrency(toRef(() => props.accountId));

const accountSource = computed(() => doc(collection<Account>(db, "accounts"), props.accountId));
const { data: account } = useDocument<Account>(accountSource);

const showManageDialog = ref(false);
const showImportDialog = ref(false);
const showAIDialog = ref(false);
const showInitialAmountDialog = ref(false);
const categoryForInitialAmount = ref<TrackedCategoryRow | null>(null);
const updatingCategoryId = ref<string | null>(null);
const exportingExcel = ref(false);

const pendingSeeds = ref<Array<{ name: string; amount: number; groupName?: string }>>([]);

const onCategoriesImported = (
  cats: Array<{ name: string; amount: number; groupName?: string }>,
) => {
  pendingSeeds.value = cats;
  showManageDialog.value = true;
};

const OTHER_KEY = "__other__";

const groupNameById = computed(() => new Map(groups.value.map((g) => [g.id, g.name])));
const groupIds = computed(() => new Set(groups.value.map((g) => g.id)));

const groupName = (key: string) => {
  if (key === OTHER_KEY) return t("categories.manage.otherGroup");
  return groupNameById.value.get(key) ?? "";
};

const resolveGroupKey = (groupId: string | undefined) =>
  groupId && groupIds.value.has(groupId) ? groupId : OTHER_KEY;

const hasAnyGroup = computed(() => groups.value.length > 0);
const groupBy = computed(() => (hasAnyGroup.value ? [{ key: "groupKey" }] : []));

const formatPercentage = (percentage: number) => {
  if (percentage > 100) return Math.ceil(percentage);
  return Math.floor(percentage);
};

const progressColorForPercentage = (percentage: number) => {
  if (percentage > 100) return "error";
  if (percentage >= 100) return "#4CAF50";
  return "primary";
};

const percentageClassForPercentage = (percentage: number) => {
  if (percentage > 100) return "text-error";
  if (percentage >= 100) return "text-[#4CAF50]";
  return "";
};

const remainingClassForAmount = (remaining: number) => {
  if (remaining < 0) return "text-error";
  return "";
};

const formatAmount = (amount: number) => {
  return new Intl.NumberFormat(locale.value, {
    style: "currency",
    currency: currencyCode.value ?? "EUR",
  }).format(amount);
};

const figuresFor = (used: number, amount: number, percentage: number) => {
  const remaining = amount - used;
  return {
    used,
    remaining,
    percentage,
    percentageLabel: formatPercentage(percentage),
    barValue: Math.min(percentage, 100),
    progressColor: progressColorForPercentage(percentage),
    percentageClass: percentageClassForPercentage(percentage),
    remainingClass: remainingClassForAmount(remaining),
    usedLabel: formatAmount(used),
    targetLabel: formatAmount(amount),
    remainingLabel: formatAmount(remaining),
  };
};

const rowsWithGroupKey = computed(() =>
  categories.value.map((c) => {
    let used = c.currentAmount;
    if (c.completedAt && c.currentAmount < c.amount) used = c.amount;
    let percentage: number;
    if (c.amount === 0) percentage = c.completedAt ? 100 : 0;
    else percentage = (used / c.amount) * 100;
    return {
      ...c,
      groupKey: resolveGroupKey(c.groupId),
      ...figuresFor(used, c.amount, percentage),
      overTarget: percentage > 100,
      showCompleted: Boolean(c.completedAt) && percentage <= 100,
    };
  }),
);

const grandFigures = computed(() => {
  const used = rowsWithGroupKey.value.reduce((sum, row) => sum + row.used, 0);
  const amount = rowsWithGroupKey.value.reduce((sum, row) => sum + row.amount, 0);
  let percentage = 0;
  if (amount !== 0) percentage = (used / amount) * 100;
  return figuresFor(used, amount, percentage);
});

const groupViews = computed(() => {
  const totals = new Map<string, { name: string; amount: number; used: number }>();
  for (const row of rowsWithGroupKey.value) {
    const entry = totals.get(row.groupKey) ?? {
      name: groupName(row.groupKey),
      amount: 0,
      used: 0,
    };
    entry.amount += row.amount;
    entry.used += row.used;
    totals.set(row.groupKey, entry);
  }

  const views: Record<string, { name: string } & ReturnType<typeof figuresFor>> = {};
  for (const [key, entry] of totals) {
    let percentage = 0;
    if (entry.amount !== 0) percentage = (entry.used / entry.amount) * 100;
    views[key] = { name: entry.name, ...figuresFor(entry.used, entry.amount, percentage) };
  }
  return views;
});

const headers = computed(() => {
  const usedTargetRemaining = [
    {
      title: t("categories.table.used"),
      key: "used",
      sortable: true,
      sortRaw: (a: { used: number }, b: { used: number }) => a.used - b.used,
    },
    {
      title: t("categories.table.target"),
      key: "target",
      sortable: true,
      sortRaw: (a: { amount: number }, b: { amount: number }) => a.amount - b.amount,
    },
    {
      title: t("categories.table.remaining"),
      key: "remaining",
      sortable: true,
      sortRaw: (a: { remaining: number }, b: { remaining: number }) => a.remaining - b.remaining,
    },
    {
      title: t("categories.table.progress"),
      key: "percentage",
      sortable: true,
      sortRaw: (a: { percentage: number }, b: { percentage: number }) =>
        a.percentage - b.percentage,
    },
    { title: t("common.actions"), key: "actions", sortable: false, align: "end" as const },
  ];
  if (!hasAnyGroup.value) {
    return [
      { title: t("categories.table.category"), key: "name", sortable: true },
      ...usedTargetRemaining,
    ];
  }
  return [
    {
      key: "data-table-group",
      title: t("categories.table.category"),
      sortable: false,
    },
    ...usedTargetRemaining,
  ];
});

const categoryRoute = (categoryId: string) => ({
  name: "Transactions",
  params: { accountId: props.accountId },
  query: { category: categoryId },
});

const categoryDocRef = (categoryId: string) =>
  doc(collection<TrackedCategory>(db, `accounts/${props.accountId}/trackedCategories`), categoryId);

const openInitialAmountDialog = (category: TrackedCategoryRow) => {
  categoryForInitialAmount.value = category;
  showInitialAmountDialog.value = true;
};

const saveInitialAmount = async (value: number | null) => {
  const category = categoryForInitialAmount.value;
  if (!category) return;

  try {
    updatingCategoryId.value = category.id;
    await updateDoc(categoryDocRef(category.id), {
      initialAmount:
        typeof value === "number" && Number.isFinite(value) && value !== 0 ? value : deleteField(),
      updatedAt: serverTimestamp(),
    });
    showInitialAmountDialog.value = false;
    categoryForInitialAmount.value = null;
  } catch (cause) {
    const error = new Error(`Error updating initial amount for category ${category.id}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    updatingCategoryId.value = null;
  }
};

const toggleCompleted = async (category: TrackedCategoryRow) => {
  try {
    updatingCategoryId.value = category.id;
    await updateDoc(categoryDocRef(category.id), {
      completedAt: category.completedAt ? deleteField() : serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (cause) {
    const error = new Error(`Error toggling completed for category ${category.id}`, { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    updatingCategoryId.value = null;
  }
};

const exportFileBaseName = computed(() => {
  const raw = account.value?.name?.replace(/[/\\?%*:|"<>]/g, "-").trim();
  return raw ? `${raw}-${props.i18nPrefix}` : `${props.i18nPrefix}-${props.accountId}`;
});

const handleExportExcel = () => {
  if (categories.value.length === 0) return;

  try {
    exportingExcel.value = true;
    const includeGroup = hasAnyGroup.value;
    const headerRow = includeGroup
      ? [
          t("categories.table.group"),
          t("categories.table.category"),
          t("categories.table.used"),
          t("categories.table.target"),
          t("categories.table.remaining"),
          t("categories.table.progress"),
        ]
      : [
          t("categories.table.category"),
          t("categories.table.used"),
          t("categories.table.target"),
          t("categories.table.remaining"),
          t("categories.table.progress"),
        ];

    const bodyRows = rowsWithGroupKey.value.map((row) => {
      const cells = [row.name, row.used, row.amount, row.remaining, row.percentageLabel];
      if (includeGroup) return [groupViews.value[row.groupKey]?.name ?? "", ...cells];
      return cells;
    });

    downloadCategoriesXlsxFile({
      filename: `${exportFileBaseName.value}.xlsx`,
      sheetName: t(`account.tabs.${props.i18nPrefix}`),
      rows: [headerRow, ...bodyRows],
    });
  } catch (cause) {
    const error = new Error("Error exporting tracked categories to Excel", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    exportingExcel.value = false;
  }
};
</script>

<style scoped>
.category-grand-total {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.5rem 2rem;
  margin-top: 0.25rem;
  margin-bottom: 1.5rem;
}

@media (max-width: 720px) {
  .category-grand-total {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.category-grand-total__sheet {
  min-width: 0;
}

.category-grand-total__value {
  font-size: 1.875rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.025em;
  font-variant-numeric: tabular-nums;
}

.category-grand-total__progress-row {
  min-height: 2.125rem;
  align-items: center;
}

.category-grand-total__bar {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.category-grand-total__pct {
  font-size: 1.875rem;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.025em;
  font-variant-numeric: tabular-nums;
  min-width: 3.25ch;
  text-align: right;
}

.category-grand-total__label {
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-top: 0.375rem;
  opacity: 0.55;
}

.category-row-actions {
  z-index: 1;
}

.display-on-row-hover {
  opacity: 0;
}
tbody tr:hover .display-on-row-hover {
  opacity: 1;
}
</style>
